/*
  Back in stock: the Notify me dialog on the product page
  (snippets/product-notify-me.liquid).

  The swap between Add to cart and Notify me is CSS, keyed on the disabled
  state the theme's own variant script already sets. This script does the rest:
  it opens and closes the dialog, writes the selected variant into the lead's
  tags and note, tells "sold out" from "this combination does not exist",
  sends the emailed copy, files the request with Klaviyo's back-in-stock
  feature, and shows the confirmation once the form is sent.
*/
if (!customElements.get('notify-me')) {
  customElements.define(
    'notify-me',
    class NotifyMe extends HTMLElement {
      // Set on submit, read on the page Shopify sends the visitor back to.
      static get FLAG() {
        return 'nb:notify-me';
      }

      connectedCallback() {
        this.dialog = this.querySelector('dialog');
        if (!this.dialog) return;

        this.form = this.dialog.querySelector('form');
        this.mail = this.querySelector('.notify-me__mail');

        try {
          this.variants = JSON.parse(this.querySelector('[data-notify-variants]')?.textContent || '[]');
        } catch (error) {
          this.variants = [];
        }

        // Delegated, because the sticky bar's button is replaced on every variant change.
        this.onDocumentClick = (event) => {
          const opener = event.target.closest('[data-notify-open]');
          if (!opener) return;
          event.preventDefault();
          this.open();
        };
        document.addEventListener('click', this.onDocumentClick);

        this.dialog.addEventListener('click', (event) => {
          // A click on the backdrop lands on the dialog element itself.
          if (event.target === this.dialog || event.target.closest('[data-notify-close]')) this.close();
        });

        this.form?.addEventListener('submit', () => this.onSubmit());
        // Kept current as the visitor types, so the note is right whoever reads the form first.
        this.form?.addEventListener('input', () => this.fill());

        this.watchSubmitButton();
        this.showResult();
      }

      disconnectedCallback() {
        document.removeEventListener('click', this.onDocumentClick);
        this.observer?.disconnect();
      }

      get productForm() {
        return document.getElementById(`product-form-${this.dataset.section}`);
      }

      // "Unavailable" is a variant combination that does not exist: nothing to be notified about.
      watchSubmitButton() {
        const button = document.getElementById(`ProductSubmitButton-${this.dataset.section}`);
        const wrapper = button?.closest('.product-form__buttons');
        if (!button || !wrapper) return;

        const sync = () => {
          const unavailable = window.variantStrings?.unavailable;
          const label = button.textContent.trim();
          wrapper.classList.toggle('is-unavailable', button.disabled && Boolean(unavailable) && label === unavailable.trim());
        };

        this.observer = new MutationObserver(sync);
        this.observer.observe(button, { attributes: true, attributeFilter: ['disabled'], childList: true, characterData: true, subtree: true });
        sync();
      }

      currentVariant() {
        const id = this.productForm?.querySelector('[name="id"]')?.value;
        return this.variants.find((variant) => String(variant.id) === String(id)) || null;
      }

      // A tag cannot hold a comma: it would be read as two tags.
      tag(text) {
        return text.replace(/,/g, ' ').replace(/\s+/g, ' ').trim().slice(0, 250);
      }

      set(selector, value) {
        const field = this.querySelector(selector);
        if (field) field.value = value;
      }

      // Writes the selected variant, and whatever the visitor has typed, into both forms.
      fill() {
        const variant = this.currentVariant();
        const title = this.dataset.productTitle || '';
        const option = !variant || this.hasAttribute('data-single-variant') ? '' : variant.title;
        const sku = variant?.sku || '';
        const link = variant ? `${this.dataset.productUrl}?variant=${variant.id}` : this.dataset.productUrl;
        const email = this.form?.querySelector('[name="contact[email]"]')?.value.trim() || '';
        const phone = this.querySelector('[data-notify-phone]')?.value.trim() || '';

        const tags = ['notify me', this.tag(title)];
        if (option) tags.push(this.tag(`${title} - ${option}`));
        // Also as a tag, so the number is on the lead even where the note is not kept.
        if (phone) tags.push(this.tag(`phone: ${phone}`));
        this.set('[data-notify-tags]', tags.join(','));

        const note = [`Notify me: ${title}${option ? ` - ${option}` : ''}`];
        if (sku) note.push(`SKU: ${sku}`);
        if (phone) note.push(`Phone: ${phone}`);
        note.push(link);
        this.set('[data-notify-note]', note.join(' | '));

        this.set('[data-notify-option]', option);
        this.set('[data-notify-sku]', sku);
        this.set('[data-notify-link]', link);
        this.set('[data-notify-mail-email]', email);
        this.set('[data-notify-mail-phone]', phone);

        const label = this.querySelector('[data-notify-option-label]');
        if (label) {
          label.textContent = option;
          label.hidden = option === '';
        }
      }

      /*
        Nothing is prevented here. The lead is the form's own submission and
        goes through Shopify's sign-up check exactly as the newsletter form
        does; this only makes sure the fields are current, leaves the flag the
        confirmation needs, and sends the emailed copy on its way. keepalive
        lets that request finish while the page is already leaving.
      */
      onSubmit() {
        this.fill();

        try {
          sessionStorage.setItem(NotifyMe.FLAG, this.dataset.section);
        } catch (error) {
          /* private browsing: the confirmation is then not reopened */
        }

        // Shopify's check can raise the event a second time; the copy goes once.
        if (this.mailed) return;
        this.mailed = true;
        try {
          this.subscribeBackInStock();
        } catch (error) {
          /* the emailed copy below still goes */
        }
        if (!this.mail) return;
        fetch(this.mail.getAttribute('action') || '/contact', {
          method: 'POST',
          body: new FormData(this.mail),
          keepalive: true,
        }).catch(() => {});
      }

      // Klaviyo's own request for this variant, which its Back in Stock flow answers.
      subscribeBackInStock() {
        const key = this.dataset.klaviyo;
        const variant = this.currentVariant();
        const email = this.form?.querySelector('[name="contact[email]"]')?.value.trim();
        if (!key || !variant || !email) return;

        const body = {
          data: {
            type: 'back-in-stock-subscription',
            attributes: {
              channels: ['EMAIL'],
              profile: { data: { type: 'profile', attributes: { email } } },
            },
            relationships: {
              variant: { data: { type: 'catalog-variant', id: `$shopify:::$default:::${variant.id}` } },
            },
          },
        };
        fetch(`https://a.klaviyo.com/client/back-in-stock-subscriptions?company_id=${encodeURIComponent(key)}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/vnd.api+json', revision: '2026-07-15' },
          body: JSON.stringify(body),
          keepalive: true,
        }).catch(() => {});
      }

      // Back from a submission of this form: the confirmation, or the error, where the form was.
      showResult() {
        let sent = false;
        try {
          sent = sessionStorage.getItem(NotifyMe.FLAG) === this.dataset.section;
          sessionStorage.removeItem(NotifyMe.FLAG);
        } catch (error) {
          sent = false;
        }
        if (!sent) return;

        const posted = this.querySelector('[data-notify-posted]');
        const failed = this.querySelector('[data-notify-error]');
        if (posted) {
          posted.hidden = false;
          const fields = this.querySelector('[data-notify-fields]');
          if (fields) fields.hidden = true;
        } else if (failed) {
          failed.hidden = false;
        } else {
          return;
        }

        this.show();
        posted?.querySelector('[tabindex="-1"]')?.focus();
        this.dropPostedFlag();
      }

      open() {
        this.fill();
        this.show();
      }

      show() {
        if (this.dialog.open) return;
        if (typeof this.dialog.showModal === 'function') {
          this.dialog.showModal();
        } else {
          this.dialog.setAttribute('open', '');
        }
      }

      close() {
        if (typeof this.dialog.close === 'function') {
          this.dialog.close();
        } else {
          this.dialog.removeAttribute('open');
        }
      }

      // So a reload of the page does not show the confirmation a second time.
      dropPostedFlag() {
        try {
          const url = new URL(window.location.href);
          if (!url.searchParams.has('customer_posted') && !url.searchParams.has('contact_posted')) return;
          url.searchParams.delete('customer_posted');
          url.searchParams.delete('contact_posted');
          url.hash = '';
          window.history.replaceState(window.history.state, '', url.toString());
        } catch (error) {
          /* the address stays as it is */
        }
      }
    }
  );
}
